<script lang="ts">
import { defineComponent, reactive,ref, onMounted, onBeforeUnmount, computed, PropType,watch } from 'vue';
import { Media,defaultMedia,ExternalIdProviders,ExternalId } from '@/types/Media';
import { Progress } from '@/components/ui/progress'
import MultimediaIcon from 'vue-material-design-icons/Multimedia.vue';
import 'video.js/dist/video-js.css';
import videojs from 'video.js'
import 'videojs-hotkeys';
interface SystemImage {
	text:string;
	percentage?:number
}

export default defineComponent({
	name: 'MediaPlayer',
	components: {
		Progress,MultimediaIcon
	},
	props: {
		profile: {
			type: Number,
			default: 11
		},
		size: {
			type: Number,
			default: 64
		},
		aspectRatio: {
			type: String,
			default: null,
			required:false
		},
		showTransfers: {
			type: Boolean,
			default: false
		},
		percentage: {
			type: Number,
			default: 0
		},
		mediaItem: {
			type: Object as PropType<Media>, // Use `PropType` to define the type
			default: () => defaultMedia, // Ensure `defaultMedia` matches the `Media` structure
		},
	},
	setup(props) {
		const displayData = ref<SystemImage>({
			    text: 'Processing ' + (props.mediaItem.filetype === 2 ? 'Video...' : 
				                              props.mediaItem.filetype === 1 ? 'Image...' : 
				                              'Content...'),
			    percentage: Math.ceil(props.percentage)
		});
		watch(
			() => props.percentage,
			(newPercentage,oldPercentage) => {
				if (oldPercentage<newPercentage) {
					displayData.value.text="Processing "
					if (props.mediaItem.filetype==2) {
					displayData.value.text+="Video..."
					}
					else if (props.mediaItem.filetype==1) {
					displayData.value.text+="Photo..."
					} else {
					displayData.value.text+="Content..."
					}
					displayData.value.percentage = Math.ceil(newPercentage);
				} else {
					if(newPercentage==0) {
						displayData.value.text="Storing Renditions..";
						displayData.value.percentage = 100;
						getThumbUrl();
					}
				}
				if(props.mediaItem.status==3) {
					Object.assign(mediaInfo,props.mediaItem);
					getThumbUrl();
				}
			}
		);
		const videoElement = ref<HTMLVideoElement | null>(null);
		const player = ref<any>(null); 
		const aspectRatio = ref('16:9'); // Default value
		const aspectRatioClass = computed(() => {
			if (props.aspectRatio) {
				// If `props.aspectRatio` is provided, set it to `aspectRatio.value`
				aspectRatio.value = props.aspectRatio;
			} else {
				// If not, assign a default value based on the condition
				aspectRatio.value = '9:16';
			}

			// Return the corresponding class name based on the `aspectRatio.value`
			return aspectRatio.value === '9:16' ? 'aspect-ratio-16x9 max-h-[500px]' : 'aspect-ratio-16x9';
		});

		const isObject = (value: any): boolean => {
			return value && typeof value === 'object' && !Array.isArray(value);
		};

		const mediaInfo = reactive<Media>(props.mediaItem);
		/*
		const externalIds = computed(() => {
			return props.mediaItem.externalids ?? [];
		});

		const hasExternalIdProvider = (provider: string): boolean => {
			return externalIds.value.some((item) => provider in item);
		};*/
		const externalIds = computed(() => {
			const ids = props.mediaItem.externalids;
			// Normalize to an array
			return Array.isArray(ids) ? ids : typeof ids === 'object' && ids !== null ? [ids] : [];
		});

		const hasExternalIdProvider = (provider: string): boolean => {
			// Safely use `.some()` after normalization
			return externalIds.value.some((item: ExternalId | ExternalIdProviders) => provider in item);
		};

		const displayUrl = ref('');
		const percentage = ref(0);
		const playerOptions = ref({
			height: '512',
			fluid: true,
			autoplay: false,
			muted: true,
			controls: true,
			language: 'en',
			sources: [] as { type: string; src: string }[], // Explicit type for sources
			poster: '',
			aspectRatio: '16:9',
			playbackRates: [0.25, 0.5, 1.0, 1.5, 2.0],
		});
		const handleImageDragStart = (event: DragEvent) => {
			event.preventDefault(); // Prevent the image from triggering its own drag behavior
		};

		const getThumbUrl = () => {
			if(props.mediaItem.status==1) {
				displayData.value.text="Queued"; 
			}

			displayUrl.value = props.mediaItem.thumbUrl+"/"+props.profile;

			if (props.mediaItem.status === 0) {
				displayUrl.value = 'https://assets.newspark.ca/2/v6/gfx/uploading.png';
			} else if ( props.mediaItem.metadataArray?.system?.widenVideoPoster) {
				displayUrl.value = props.mediaItem.metadataArray.system.widenVideoPoster;
			} else if (props.mediaItem.status === 1) {
				displayUrl.value = 'https://assets.newspark.ca/2/v6/gfx/queue.png';
			} else if (props.mediaItem.status === 2 && props.mediaItem.extension !== 'svg') {
				displayUrl.value = '';
			} else if (props.mediaItem.status === 4) {
				displayUrl.value = 'https://assets.newspark.ca/2/v6/gfx/failed3.png';
			} else if (props.mediaItem.filetype != 1 && props.mediaItem.filetype != 2) {
				switch (props.mediaItem.extension) {
					case 'pptx':
					case 'ppt':
					case 'pptm':
						displayUrl.value = 'https://assets.newspark.ca/2/v6/gfx/ppt11.svg';
						break;
					case 'pdf':
						displayUrl.value = 'https://assets.newspark.ca/2/v6/gfx/pdf11.svg';
						break;
					case 'zip':
						displayUrl.value = 'https://assets.newspark.ca/2/v6/gfx/zip11.svg';
						break;
					case 'xlsx':
						displayUrl.value = 'https://assets.newspark.ca/2/v6/gfx/xls11.svg';
						break;
					case 'csv':
						displayUrl.value = 'https://assets.newspark.ca/2/v6/gfx/excel11.svg';
						break;
					case 'psd':
						displayUrl.value = 'https://assets.newspark.ca/2/v6/gfx/psd11.svg';
						break;
					case 'doc':
					case 'docx':
						displayUrl.value = 'https://assets.newspark.ca/2/v6/gfx/doc11.svg';
						break;
					case 'txt':
						displayUrl.value = 'https://assets.newspark.ca/2/v6/gfx/txt11.svg';
						break;
					case 'vtt':
						displayUrl.value = 'https://assets.newspark.ca/2/v6/gfx/cc11.svg';
						break;
					case 'ai':
						displayUrl.value = 'https://assets.newspark.ca/2/v6/gfx/ai11.svg';
						break;
					case 'mp3':
						displayUrl.value = 'https://assets.newspark.ca/2/v6/gfx/mp311.svg';
						break;
					default:
						displayUrl.value = 'https://assets.newspark.ca/2/v6/gfx/word11.svg';
						break;
				}
			} else if (props.mediaItem.extension === 'svg') {
				displayUrl.value = 'https://assets.newspark.ca/2/v6/gfx/svg11.svg';
			}

			playerOptions.value.sources = [
				{ type: 'video/mp4', src: `${props.mediaItem.thumbUrl.split('/11')[0]}/22` },
			];
			if (props.mediaItem.metadata?.system?.thumbnail) {
				playerOptions.value.poster = `/i/${props.mediaItem.metadata.system.thumbnail}/t/11/w/500`;
			} else {
				playerOptions.value.poster = `${props.mediaItem.thumbUrl.split('/'+props.profile)[0]}/${props.profile}`;
			}
		};

		// Re-compute thumb URL whenever the media item changes (e.g. after async load)
		watch(() => [props.mediaItem.id, props.mediaItem.status, props.mediaItem.thumbUrl], () => {
			if (props.mediaItem.id && props.mediaItem.thumbUrl) {
				Object.assign(mediaInfo, props.mediaItem);
				getThumbUrl();
			}
		}, { immediate: false });

		// Initialize video player when element becomes available (after v-if renders)
		watch(videoElement, (el) => {
			if (el && !player.value && props.mediaItem.filetype === 2 && props.mediaItem.status === 3 && props.mediaItem.thumbUrl) {
				player.value = videojs(el, {
					fluid: true,
					responsive: true,
					autoplay: false,
					muted: true,
					controls: true,
					language: 'en',
					sources: [{ type: 'video/mp4', src: `${props.mediaItem.thumbUrl.split('/'+props.profile)[0]}/22` }],
				});
				el.addEventListener('loadedmetadata', () => {
					const videoWidth = el?.videoWidth || 0;
					const videoHeight = el?.videoHeight || 0;
					if (videoWidth && videoHeight) {
						aspectRatio.value = videoWidth > videoHeight ? '16:9' : '9:16';
					}
				});
			}
		});

		onMounted(() => {
			getThumbUrl();
			if (videoElement.value && props.mediaItem.filetype === 2 && props.mediaItem.status === 3) {
				player.value = videojs(videoElement.value, {
					fluid: true,
					responsive: true,
					autoplay: false,
					muted: true,
					controls: true,
					language: 'en',
					sources: [{ type: 'video/mp4', src: `${props.mediaItem.thumbUrl.split('/'+props.profile)[0]}/22` }],
				});
				videoElement.value.addEventListener('loadedmetadata', () => {
					const videoWidth = videoElement.value?.videoWidth || 0;
					const videoHeight = videoElement.value?.videoHeight || 0;

					if (videoWidth && videoHeight) {
						aspectRatio.value = videoWidth > videoHeight ? '16:9' : '9:16';
					}
				});
			}

		});
		onBeforeUnmount(() => {
			if (player.value) {
				player.value.dispose();
			}
		});

		return {
			handleImageDragStart,
			aspectRatioClass,
			isObject,
			percentage,
			videoElement,
			displayData,
			displayUrl,
			mediaInfo,
			playerOptions,
			MultimediaIcon,
			Progress,
			getThumbUrl,
			externalIds,
			hasExternalIdProvider
		};
	},
});
</script>

<template>
	<div class="w-full" v-if="!showTransfers">
		<div v-if="mediaItem.filetype === 2 && mediaItem.status === 3">
			<div class="video-player-container aspect-video"  :class="aspectRatioClass">
				<video ref="videoElement" class="video-js vjs-default-skin  " :poster="playerOptions.poster" ></video>
			</div>
		</div>

		<div v-else-if="mediaItem.filetype === 3 && mediaItem.status === 3">
			<img :src="displayUrl" class="object-cover border border-card-foreground bg-background" :alt="mediaItem.title"  @dragstart.prevent />
			<audio controls class="w-100" style="height: 20px">
				<source :src="mediaItem.thumbUrl+'/30'" type="audio/mpeg" />
				Your browser does not support the audio element.
			</audio>
		</div>

		<div v-else class="imgContainer w-full">
			<img :src="displayUrl" class="object-cover border border-card-foregroubd  bg-background" :alt="mediaItem.title"  @dragstart="handleImageDragStart" v-if="mediaItem.status>2"/>
			<div class="aspect-video border rounded border-primary flex flex-col justify-center items-center w-full" v-else  @dragstart="handleImageDragStart">
				<div class="py-2"><MultimediaIcon class="text-primary" :size="size"/></div>
				<div class="p-2   text-foreground leading-title" :class="{'text-headline' :size>100, 'text-subtitle': size<100}">{{displayData.text}}</div>
				<div class="p-2 " v-if="mediaInfo.status==1 && size>100">
					<svg class="mr-3 -ml-1 size-24 animate-spin text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
				</div>
				<div v-else-if="mediaInfo.status==2 && size>100" class="p-2 text-display"> {{displayData.percentage}}% </div>
				<div class="w-3/4 py-1" v-if="mediaInfo.status==2"><Progress v-model="displayData.percentage" class="h-4 w-full  text-primary"/></div>
			</div>

		</div>
	</div>
	<div class="relative w-full h-full" v-else>
		<!-- Thumbnail -->
		<img :src="displayUrl" :alt="mediaItem.title" class="w-full h-full object-cover border border-card-foreground"  v-if="mediaItem.status>2">
		<div class="aspect-video border rounded border-primary flex flex-col justify-center items-center w-full" v-else  @dragstart="handleImageDragStart">
			<div class="py-2 "><MultimediaIcon class="text-primary" :size="size"/></div>
			<div class="p-2  text-foreground w-full text-center leading-title" :class="{'text-headline' :size>100, 'text-subtitle': size<100}">{{displayData.text}}</div>
			<div class="p-2 " v-if="mediaInfo.status==1 && size>100">
				<svg class="mr-3 -ml-1 size-24 animate-spin text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
			</div>          
			<div v-else-if="mediaInfo.status==2 && size>100" class="p-2  text-display"> {{displayData.percentage}}% </div>
			<div class="w-3/4 py-1" v-if="mediaInfo.status==2"><Progress v-model="displayData.percentage" class="h-4 w-full  text-primary"/></div>
		</div>  


		<!-- Extension badge removed — shown by parent (MediaList) instead -->

			<!-- Bottom-Right Icons -->
			<div class="absolute bottom-1 right-1 flex  px-2 py-1 bg-black  bg-opacity-70  rounded-full space-x-1" v-if="mediaItem.externalids && isObject(mediaItem.externalids)"> 
				<img v-if="hasExternalIdProvider('youtube')" src="https://assets.newspark.ca/2/v51/gfx/youtube.ico" alt="YouTube" class="w-5 h-5 ">
				<img v-if="hasExternalIdProvider('widen')" src="https://assets.newspark.ca/2/v51/gfx/acquia.ico" alt="Acquia" class="w-5 h-5 ">
				<img v-if="hasExternalIdProvider('brightcove')" src="https://assets.newspark.ca/2/v51/gfx/bc-icon.png" alt="Brightcove" class="w-5 h-5 ">
				<img v-if="hasExternalIdProvider('jwplayer')" src="https://assets.newspark.ca/2/v51/gfx/jwplayer.ico" alt="JWPlayer" class="w-5 h-5 ">
			</div>
	</div>

</template>

<style scoped>
.imgContainer {
	position: relative;
}

.bottomright {
	position: absolute;
	bottom: 8px;
	right: 16px;
}
.video-player-container {
	position: relative;
	width: 100%;
	height: 0;
	overflow: hidden;
	padding-top: 56.25%; /* Default aspect ratio for 16:9 (16 / 9 * 100) */
}

.video-js {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
}

/* Aspect ratio handling for 9x16 videos */
.aspect-ratio-9x16 {
	padding-top: 177.78%; /* 9/16 * 100 for 9:16 aspect ratio */
}

/* Aspect ratio handling for 16x9 videos */
.aspect-ratio-16x9 {
	padding-top: 56.25%; /* 16/9 * 100 for 16:9 aspect ratio */
}

/* Ensure that the video maintains its height for 9x16 */
.aspect-ratio-9x16 .video-js {
	object-fit: contain; /* Prevent overflow for vertical videos */
	max-height: 100%;
}
</style>
